import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Bot, Copy, CheckCircle2, Play, Pause, AlertTriangle, TrendingUp, Mail, Loader2, Zap } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { FIRST_CLIENT_EMAILS, OUTBOUND_STRATEGY } from "@/content/firstClientEmails";

export default function Autopilot() {
    const { locale } = useI18n();
    const [state, setState] = useState(null);
    const [busy, setBusy] = useState(false);
    const [copiedId, setCopiedId] = useState(null);
    const bl = (en, fr) => (locale === "fr" ? fr : en);

    async function load() {
        try {
            const r = await api.get("/autopilot/state");
            setState(r.data);
        } catch (_e) { /* silent */ }
    }
    useEffect(() => {
        load();
        const t = setInterval(load, 15000);
        return () => clearInterval(t);
    }, []);

    async function toggle() {
        setBusy(true);
        try {
            if (state?.enabled) {
                await api.post("/autopilot/disable");
                toast.success(bl("Autopilot paused", "Autopilote en pause", locale));
            } else {
                if (!state?.infra_ready) {
                    toast.error(bl("Configure RESEND_API_KEY + DNS first", "Configure RESEND_API_KEY + DNS d'abord", locale));
                    setBusy(false);
                    return;
                }
                await api.post("/autopilot/enable", { daily_limit: 20 });
                toast.success(bl("Autopilot enabled — max 20/day", "Autopilote activé — max 20/jour", locale));
            }
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally { setBusy(false); }
    }

    async function copy(text, id) {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedId(id);
            toast.success(bl("Copied", "Copié", locale));
            setTimeout(() => setCopiedId(null), 2500);
        } catch (_e) { toast.error("Copy failed"); }
    }

    return (
        <div className="px-6 lg:px-10 py-6 max-w-6xl mx-auto">
            <div className="mb-6">
                <div className="text-xs uppercase tracking-[0.22em] font-bold text-brand-cyan flex items-center gap-2 mb-1">
                    <Bot size={13} /> AUTOPILOTE IA — DÉCROCHE TON PREMIER CLIENT
                </div>
                <h1 className="font-display font-black text-3xl tracking-tighter">
                    5 emails écrits pour toi + un pilote IA qui envoie tout seul
                </h1>
                <p className="mt-2 text-sm text-zinc-400 max-w-3xl leading-relaxed">
                    Les 5 emails ci-dessous sont **prêts à envoyer** — copie, personnalise `{{prenom}}` et `{{societe}}`, expédie.
                    L'autopilote IA prend le relais : rate-limit safe, classification GPT des réponses, arrêt auto sur signal négatif.
                </p>
            </div>

            {/* Autopilot control */}
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className={`rounded-2xl p-6 mb-6 border ${state?.enabled ? "bg-emerald-500/10 border-emerald-500/30" : "bg-brand-surface border-white/10"}`}
                data-testid="autopilot-control"
            >
                <div className="flex items-start justify-between flex-wrap gap-4">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <div className={`w-2.5 h-2.5 rounded-full ${state?.enabled ? "bg-emerald-400 animate-pulse" : "bg-zinc-500"}`} />
                            <div className="font-display font-black text-2xl tracking-tighter">
                                {state?.enabled ? bl("Autopilot ACTIVE", "Autopilote ACTIF") : bl("Autopilot paused", "Autopilote en pause")}
                            </div>
                        </div>
                        <p className="text-sm text-zinc-400 max-w-lg">
                            {state?.enabled
                                ? bl("Sends up to 20 emails/day. Classifies replies. Stops on negative signals.",
                                     "Envoie jusqu'à 20 emails/jour. Classifie les réponses. S'arrête sur signal négatif.", locale)
                                : bl("The AI is idle. Click to start.", "L'IA est au repos. Clique pour démarrer.", locale)}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={toggle}
                        disabled={busy}
                        data-testid="autopilot-toggle"
                        className={`inline-flex items-center gap-2 px-5 py-3 rounded-full font-semibold text-sm disabled:opacity-60 ${state?.enabled ? "bg-white/10 hover:bg-white/15 text-white" : "btn-cta text-white"}`}
                    >
                        {busy ? <Loader2 size={16} className="animate-spin" /> : state?.enabled ? <Pause size={16} /> : <Play size={16} />}
                        {state?.enabled ? bl("Pause", "Mettre en pause") : bl("Enable autopilot", "Activer l'autopilote")}
                    </button>
                </div>

                {!state?.infra_ready && (
                    <div className="mt-4 rounded-xl p-4 bg-amber-500/10 border border-amber-500/25 text-sm text-amber-200 flex gap-2 items-start" data-testid="autopilot-infra-missing">
                        <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                        <div>
                            <div className="font-semibold">Configuration requise avant activation :</div>
                            <ul className="mt-2 space-y-1 text-xs text-amber-100/90 list-disc pl-5">
                                <li>Créer un compte Resend gratuit → récupérer la clé API (`re_xxxxx...`)</li>
                                <li>Ajouter <code className="font-mono">RESEND_API_KEY</code> dans les variables serveur</li>
                                <li>Configurer SPF + DKIM + DMARC sur boxe-b2b.com (Resend fournit le TXT à copier)</li>
                                <li>Vérifier le domaine dans le dashboard Resend</li>
                            </ul>
                        </div>
                    </div>
                )}

                {state?.enabled && state?.quotas && (
                    <div className="mt-5 grid grid-cols-2 md:grid-cols-5 gap-3">
                        <Quota label={bl("Sent today", "Envoyés aujourd'hui")} value={`${state.quotas.sent_today}/${state.daily_limit}`} />
                        <Quota label={bl("Remaining today", "Restants aujourd'hui")} value={state.quotas.remaining_today} highlight />
                        <Quota label={bl("Queued", "En file")} value={state.quotas.queued} />
                        <Quota label={bl("Sent 7d", "Envoyés 7j")} value={state.quotas.sent_week} />
                        <Quota label={bl("Positive replies", "Réponses +")} value={state?.results?.positive_replies || 0} highlight tone="cyan" />
                    </div>
                )}
            </motion.div>

            {/* Safety notes */}
            <div className="mb-6 rounded-xl p-4 bg-brand-surface border border-white/8 text-xs text-zinc-400" data-testid="autopilot-safety">
                <div className="font-bold text-zinc-200 mb-2 flex items-center gap-1.5"><AlertTriangle size={12} className="text-amber-400" /> Règles de sécurité intégrées</div>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-1.5 pl-4 list-disc marker:text-brand-cyan">
                    {(state?.safety_notes || OUTBOUND_STRATEGY.notes).map((n, i) => <li key={i}>{n}</li>)}
                </ul>
            </div>

            {/* The 5 emails */}
            <div className="text-xs uppercase tracking-widest font-bold text-zinc-500 mb-3">
                {bl("Your 5 first-client emails — copy & send", "Tes 5 emails premier client — copie et envoie")}
            </div>
            <div className="space-y-4 mb-8">
                {FIRST_CLIENT_EMAILS.map((email) => (
                    <div key={email.id} className="rounded-2xl bg-brand-surface border border-white/10 overflow-hidden" data-testid={`email-template-${email.id}`}>
                        <div className="p-4 border-b border-white/8 bg-white/[0.02] flex items-start justify-between gap-3 flex-wrap">
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-brand-cyan font-bold">EMAIL #{email.id} — {email.target}</div>
                                <div className="mt-1 text-sm text-zinc-300"><b>Où trouver dans BOXEB2B :</b> <span className="text-zinc-400 font-mono text-xs">{email.find_in_boxeb2b}</span></div>
                                <div className="mt-1 flex items-center gap-3 text-xs text-zinc-500 font-mono">
                                    <span>Cible : {email.expected_targets} leads</span>
                                    <span>·</span>
                                    <span>Réponse attendue : <span className="text-brand-cyan">{email.expected_reply_rate}</span></span>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => copy(`Sujet : ${email.subject}\n\n${email.body}`, email.id)}
                                data-testid={`email-copy-${email.id}`}
                                className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full bg-brand-indigo/20 hover:bg-brand-indigo/30 text-white border border-brand-indigo/40"
                            >
                                {copiedId === email.id ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                                {copiedId === email.id ? bl("Copied", "Copié") : bl("Copy email", "Copier")}
                            </button>
                        </div>
                        <div className="p-5">
                            <div className="text-xs text-zinc-500 mb-1">SUJET</div>
                            <div className="font-mono text-sm text-white mb-4 px-3 py-2 rounded-lg bg-brand-void border border-white/8">{email.subject}</div>
                            <div className="text-xs text-zinc-500 mb-1">CORPS</div>
                            <pre className="font-mono text-xs text-zinc-300 whitespace-pre-wrap leading-relaxed px-3 py-3 rounded-lg bg-brand-void border border-white/8">{email.body}</pre>
                        </div>
                    </div>
                ))}
            </div>

            {/* Playbook envoi */}
            <div className="rounded-2xl p-6 bg-gradient-to-br from-brand-indigo/20 to-brand-cyan/10 border border-brand-indigo/30" data-testid="autopilot-playbook">
                <div className="text-xs uppercase tracking-widest text-brand-cyan font-bold mb-2">TA STRATÉGIE 30 JOURS</div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {[
                        { week: "Semaine 1", limit: OUTBOUND_STRATEGY.max_per_day_week1, goal: "5 emails/jour — apprends le ton" },
                        { week: "Semaine 2", limit: OUTBOUND_STRATEGY.max_per_day_week2, goal: "10/jour — mesure les réponses" },
                        { week: "Semaine 3", limit: OUTBOUND_STRATEGY.max_per_day_week3, goal: "15/jour — itère les meilleurs" },
                        { week: "Semaine 4", limit: OUTBOUND_STRATEGY.max_per_day_week4, goal: "20/jour — sign 1er client" },
                    ].map(w => (
                        <div key={w.week} className="rounded-xl p-3 bg-brand-void border border-white/8">
                            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{w.week}</div>
                            <div className="mt-1 font-display font-black text-2xl tracking-tighter">{w.limit}/j</div>
                            <div className="text-xs text-zinc-400 mt-1">{w.goal}</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function Quota({ label, value, highlight, tone = "indigo" }) {
    return (
        <div className={`rounded-xl p-3 border ${highlight ? "border-brand-cyan/30 bg-brand-cyan/5" : "border-white/8 bg-brand-void"}`}>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{label}</div>
            <div className={`mt-1 font-display font-black text-xl tracking-tighter ${highlight ? "text-brand-cyan" : "text-white"}`}>{value}</div>
        </div>
    );
}
